import moment from "moment";
import i18n from "@/i18n";

import { Activity, ActivityProvider, ActivityStatus } from "./api/activities";
import { EntityType, MetadataProvider } from "./fields/metadata";
import { PresentationHelper } from "./fields/presentation";
import { Opportunity, OpportunityProvider } from "./api/opportunities";
import { Delivery, DeliveryProvider, DeliveryStatus } from "./api/deliveries";
import { SupportCase, SupportCaseProvider, SupportCaseStatus } from "./api/supportCase";
import { FieldName } from "./fields/names";
import { Context } from "./contex";
import { OrganisationSubEntityAccessData } from "./api/organisations";

export class EntityProvider {
    static DefaultPageSize = 10;

    static getProvider(type: EntityType): GenericEntityProvider<any> {
        switch (type) {
            case EntityType.Activity: return ActivityProvider;
            case EntityType.Opportunity: return OpportunityProvider;
            case EntityType.Delivery: return DeliveryProvider;
            case EntityType.SupportCase: return SupportCaseProvider;
            default: throw new Error('Entity type is not supported');
        }
    }

    static getEntityRow(type: EntityType, entity: any, includeUserTags: boolean = false): EntityRow {
        switch (type) {
            case EntityType.Activity: return this.getActivityRow(entity, includeUserTags);
            case EntityType.Opportunity: return this.getOpportunityRow(entity, includeUserTags);
            case EntityType.Delivery: return this.getDeliveryRow(entity, includeUserTags);
            case EntityType.SupportCase: return this.getSupportCaseRow(entity, includeUserTags);
            default: throw new Error('Entity type is not supported');
        }
    }

    static getStatusChangeModel(type: EntityType, id: number, value: boolean): any {
        switch (type) {
            case EntityType.Activity: return { ActivityId: id, ActivityStatus: value ? ActivityStatus.Completed : ActivityStatus.Active };
            case EntityType.Opportunity: return { OpportunityId: id, OpportunityLevel: value ? OpportunityProvider.WonLevel : +(MetadataProvider.getFieldByName(FieldName.OpportunityLevel)?.options[0]?.value || 1) };
            case EntityType.Delivery: return { DeliveryId: id, DeliveryStatus: value ? DeliveryStatus.Completed : DeliveryStatus.OnGoing };
            case EntityType.SupportCase: return { SupportCaseId: id, SupportCaseStatus: value ? SupportCaseStatus.Closed : SupportCaseStatus.Ongoing };
            default: throw new Error('Entity type is not supported');
        }
    }

    static isStatusEditable(type: EntityType) {
        let result = Context.user.accessSettings.editOwnEntities;
        switch (type) {
            case EntityType.Activity:
                result &&= Context.user.accessSettings.activitySetCompleted;
                break;
            case EntityType.Delivery:
                result &&= Context.user.accessSettings.deliverySetCompleted;
                break;
            case EntityType.SupportCase:
                result &&= Context.user.accessSettings.supportEditStatus;
                break;
        }
        return result;
    }

    static canReadAllSubEntities(type: EntityType, accessData: OrganisationSubEntityAccessData | null) {
        if (accessData == null) return false;
        switch (type) {
            case EntityType.Activity: return accessData.readAllActivities;
            case EntityType.Opportunity: return accessData.readAllOpportunities;
            case EntityType.Delivery: return accessData.readAllDeliveries;
            case EntityType.SupportCase: return accessData.readAllSupportCases;
            default: throw new Error('Entity type is not supported');
        }
    }

    static getActivityRow(activity: Activity, includeUserTags: boolean = false): EntityRow {
        return {
            id: activity.ActivityId,
            status: activity.$status || this.getEntityStatus(activity.ActivityDateTime, activity.ActivityStatus == ActivityStatus.Completed, false, activity.ActivityAllDay),
            title: activity.ActivityDescription || i18n.tc('terms.activity'),
            tags: this.getEntityTags([
                FieldName.ActivityDateTime,
                FieldName.ActivityPersonId,
                FieldName.ActivityAction,
                FieldName.ActivityType,
                includeUserTags ? FieldName.ActivityAssignedTo : '',
            ], activity, EntityType.Activity)
        }
    }

    static getOpportunityRow(opportunity: Opportunity, includeUserTags: boolean = false): EntityRow {
        return {
            id: opportunity.OpportunityId,
            status: opportunity.$status || this.getEntityStatus(opportunity.OpportunityOrderDate, opportunity.OpportunityLevel >= OpportunityProvider.WonLevel),
            title: opportunity.OpportunityDescription || i18n.tc('terms.opportunity'),
            tags: this.getEntityTags([
                FieldName.OpportunityOrderDate,
                FieldName.OpportunityPersonId,
                FieldName.OpportunityLevelText,
                FieldName.OpportunityPipelineType,
                FieldName.OpportunityNumber,
                FieldName.OpportunityOrderValue,
                includeUserTags ? FieldName.OpportunityAssignedTo : '',
            ], opportunity, EntityType.Opportunity)
        }
    }

    static getDeliveryRow(delivery: Delivery, includeUserTags: boolean = false): EntityRow {
        return {
            id: delivery.DeliveryId,
            status: delivery.$status || this.getEntityStatus(delivery.DeliveryOrderDate, delivery.DeliveryStatus == DeliveryStatus.Completed,
                delivery.DeliveryStatus == DeliveryStatus.GeneralDelivery || !MetadataProvider.getFieldByName(FieldName.DeliveryStatus)),
            title: delivery.DeliveryDescription || i18n.tc('terms.delivery'),
            tags: this.getEntityTags([
                FieldName.OpportunityOrderDate,
                FieldName.DeliveryPersonId,
                FieldName.DeliveryPhase,
                FieldName.DeliveryNumber,
                FieldName.OpportunityOrderValue,
                includeUserTags ? FieldName.DeliveryResponsible : '',
            ], delivery, EntityType.Delivery)
        }
    }

    static getSupportCaseRow(supportCase: SupportCase, includeUserTags: boolean = false): EntityRow {
        return {
            id: supportCase.SupportCaseId,
            status: supportCase.$status || this.getEntityStatus(supportCase.SupportCaseDeadline, supportCase.SupportCaseStatus == SupportCaseStatus.Closed),
            title: supportCase.SupportCaseSubject || i18n.tc('terms.support_case'),
            tags: this.getEntityTags([
                FieldName.SupportCaseDeadline,
                FieldName.SupportCasePersonId,
                FieldName.SupportCaseChannel,
                FieldName.SupportCaseAction,
                FieldName.SupportCaseTicket,
                includeUserTags ? FieldName.SupportCaseAssignedTo : '',
            ], supportCase, EntityType.SupportCase)
        }
    }

    static getEntityTags(fieldNames: string[], entity: any, entityType: EntityType) {
        return fieldNames.map(fieldName => ({
            key: fieldName,
            value: PresentationHelper.formatField(fieldName, entity, entityType),
            label: MetadataProvider.getFieldByName(fieldName)?.label || '',
            icon: MetadataProvider.getFieldIcon(fieldName),
        }) as EntityTag).filter(t => t.value?.trim());
    }

    static getEntityStatus(timestamp: string, isCompleted: boolean, isGeneral = false, isAllDayShifted = false): EntityStatus {
        if (isGeneral) return EntityStatus.General;
        if (isCompleted) return EntityStatus.Completed;

        let target = moment.utc(timestamp);
        let source = moment.utc();

        if (isAllDayShifted) {
            target = target.subtract(1, 'day');
        }

        if (target.isSame(moment(target).startOf('day'))) {
            source = source.startOf('day');
        }

        if (source.diff(target) > 0) {
            return EntityStatus.Overdue;
        }
        
        if (moment(source).startOf('day').diff(target.startOf('day'), 'days') === 0) {
            return EntityStatus.Today;
        }

        return EntityStatus.Active;
    }

    static getEntityName(type: EntityType): string {
        switch (type) {
            case EntityType.Activity: return i18n.tc('terms.activity');
            case EntityType.Opportunity: return i18n.tc('terms.opportunity');
            case EntityType.Delivery: return i18n.tc('terms.delivery');
            case EntityType.SupportCase: return i18n.tc('terms.support_case');
            default: return '';
        }
    }

    static getStaticFields(type: EntityType): FieldName[] {
        switch (type) {
            case EntityType.Activity: return [
                FieldName.ActivityDescription,
                FieldName.ActivityDateTime,
                FieldName.ActivityAction,
                FieldName.ActivityType,
                FieldName.ActivityMainProductService,
            ];
            case EntityType.Opportunity: return [
                FieldName.OpportunityDescription,
                FieldName.OpportunityOrderDate,
                FieldName.OpportunityLevel,
                FieldName.OpportunityMainProductService,
            ];
            case EntityType.Delivery: return [
                FieldName.OpportunityDescription,
                FieldName.OpportunityOrderDate,
                FieldName.OpportunityMainProductService,
            ];
            case EntityType.SupportCase: return [
                FieldName.SupportCaseSubject,
                FieldName.SupportCaseDeadline,
                FieldName.SupportCaseAction,
                FieldName.SupportCaseChannel,
                FieldName.SupportCaseMainProductService,
            ];
            default: return [];
        }
    }

    static getEmptyModel(type: EntityType, organisationId: number, personId: number, userId: number): any {
        switch (type) {
            case EntityType.Activity: return {
                ActivityId: 0,
                ActivityOrganisationId: organisationId,
                ActivityPersonId: personId,
                ActivityAssignedTo: userId,
                ActivityDateTime: moment.utc().add(1, 'day').startOf('minute').toISOString(),
            };
            case EntityType.Opportunity: return {
                OpportunityId: 0,
                OpportunityOrganisationId: organisationId,
                OpportunityPersonId: personId,
                OpportunityAssignedTo: userId,
                OpportunityOrderDate: moment.utc().add(1, 'day').startOf('day').toISOString().substring(0, 10),
            };
            case EntityType.Delivery: return {
                DeliveryId: 0,
                DeliveryOrganisationId: organisationId,
                DeliveryPersonId: personId,
                DeliveryResponsible: userId,
                DeliveryOrderDate: moment.utc().add(1, 'day').startOf('day').toISOString().substring(0, 10),
            };
            case EntityType.SupportCase: return {
                SupportCaseId: 0,
                SupportCaseOrganisationId: organisationId,
                SupportCasePersonId: personId,
                SupportCaseAssignedTo: userId,
                SupportCaseDeadline: moment.utc().add(1, 'day').startOf('minute').toISOString(),
            };
            default: throw new Error('Entity type is not supported');
        }
    }
}

export interface BaseEntity {
    $status?: EntityStatus
}

export interface GenericEntityProvider<T extends BaseEntity> {
    get(id: number): Promise<T>
    getAllByOrganisation(organisationId: number, page?: number, size?: number): Promise<T[]>
    getMyByOrganisation(organisationId: number, page?: number, size?: number): Promise<T[]>
    save(activity: T): Promise<number>
}

export interface EntityRow {
    id: number
    title: string
    status: EntityStatus
    tags: EntityTag[]
}

export enum EntityStatus {
    Disabled = -2,
    None = -1,
    General,
    Active,
    Today,
    Overdue,
    Completed,
}

export interface EntityTag {
    type?: TagType
    icon?: string
    value: string
    label?: string
}

export enum TagType {
    Default = '',
    Primary = 'primary',
    Success = 'success',
    Danger = 'danger',
    Warning = 'warning',
    Blue = 'blue',
}